import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useUser } from '../context/UserContext';
import { FaHome, FaGraduationCap, FaQuestionCircle, FaChartLine, FaPaperPlane, FaRobot, FaUser, FaExclamationTriangle, FaHistory, FaComments, FaPlus, FaTrash, FaEdit, FaBars, FaTimes, FaUsers, FaFlask } from 'react-icons/fa';
import AuthService from '../services/AuthService';
import AskService from '../services/AskService';
import PromptsHistory from './PromptsHistory';
import toast from 'react-hot-toast';
import soundEffects from '../utils/soundEffects';
import { createRipple, createSparkle, createPulse, createGlow, createSuccessBurst } from '../utils/vfx';
import BottomNav from './BottomNav';
import ProfileMenu from './ProfileMenu';
import './AskFeature.css';

const AskFeature = () => {
  const navigate = useNavigate();
  const { state } = useUser();
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [serviceAvailable, setServiceAvailable] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [previousPrompts, setPreviousPrompts] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth > 768);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) setSidebarOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // auth
  useEffect(() => {
    const initializeComponent = async () => {
      try {
        
        // check user auth
        if (!AuthService.isAuthenticated()) {
          navigate('/login', { replace: true });
          return;
        }

        // exist in content
        if (!state.user) {
          navigate('/login', { replace: true });
          return;
        }

        setIsAuthenticated(true);

      } catch (error) {
        navigate('/login', { replace: true });
      } finally {
        setLoading(false);
      }
    };

    initializeComponent();
  }, [navigate, state.user]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load conversations
  const loadConversations = async () => {
    if (!isAuthenticated || !AuthService.isAuthenticated()) return;
    
    setLoadingConversations(true);
    try {
      const response = await AskService.getConversations();
      if (response.success && response.data.conversations) {
        setConversations(response.data.conversations);
        // Don't auto-select a conversation - we create a new one when opening
      }
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      setLoadingConversations(false);
    }
  };

  // Load messages for current conversation
  const loadMessages = async (conversationId) => {
    if (!conversationId) {
      setMessages([]);
      return;
    }
    
    setLoadingHistory(true);
    try {
      const historyResponse = await AskService.getHistory(1, 50, conversationId);
      
      if (historyResponse.success && historyResponse.data.messages && historyResponse.data.messages.length > 0) {
        const formattedMessages = [];
        const prompts = [];
        
        // Create pairs of user messages and AI responses
        historyResponse.data.messages.forEach(msg => {
          // Add user message (the prompt)
          if (msg.prompt) {
            formattedMessages.push({
              id: msg.id + '_user',
              type: 'user',
              content: msg.prompt,
              timestamp: new Date(msg.createdAt)
            });
            
            // Also add to prompts for quick access
            prompts.push({
              id: msg.id,
              content: msg.prompt,
              timestamp: new Date(msg.createdAt)
            });
          }
          
          // Add AI response
          if (msg.response) {
            formattedMessages.push({
              id: msg.id + '_ai',
              type: 'ai',
              content: msg.response,
              timestamp: new Date(msg.createdAt)
            });
          }
        });
        
        // Sort by timestamp to maintain chronological order
        formattedMessages.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        prompts.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)); // Newest first for prompts
        
        setMessages(formattedMessages);
        setPreviousPrompts(prompts);
      } else {
        setMessages([]);
        setPreviousPrompts([]);
      }
    } catch (error) {
      console.error('Failed to load messages:', error);
      setMessages([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // load data after auth and create new conversation
  useEffect(() => {
    const loadData = async () => {
      if (isAuthenticated && AuthService.isAuthenticated()) {
        // load suggestions (no auth required)
        try {
          const suggestionsResponse = await AskService.getSuggestions();
          if (suggestionsResponse.success && suggestionsResponse.data.suggestions) {
            setSuggestions(suggestionsResponse.data.suggestions);
          }
        } catch (error) {
        }

        // check ai service availability (no auth required)
        try {
          const isAvailable = await AskService.isServiceAvailable();
          setServiceAvailable(isAvailable);
        } catch (error) {
          setServiceAvailable(false);
        }

        // Create a new conversation when opening the ask feature
        try {
          const newConvResponse = await AskService.createConversation('New Conversation');
          if (newConvResponse.success) {
            setCurrentConversationId(newConvResponse.data.id);
            setMessages([]); // Start with empty messages
            setPreviousPrompts([]);
          }
        } catch (error) {
          console.error('Failed to create new conversation:', error);
          toast.error('Failed to create new conversation');
        }

        // Load conversations list (for sidebar)
        await loadConversations();
      }
    };

    loadData();
  }, [isAuthenticated]);

  // Load messages when conversation changes (but not when sending messages)
  useEffect(() => {
    // Only load messages if we're not currently loading (sending a message)
    if (currentConversationId && !isLoading) {
      loadMessages(currentConversationId);
    } else if (!currentConversationId) {
      setMessages([]);
      setPreviousPrompts([]);
    }
  }, [currentConversationId]);

  const preloadedPrompts = suggestions.length > 0 ? suggestions.slice(0, 6) : [
    "What's a Roth IRA?",
    "How do taxes work?",
    "What's the difference between stocks and bonds?",
    "How do I start investing?",
    "What's compound interest?",
    "How do I build an emergency fund?"
  ];

  const sampleResponses = {
    "What's a Roth IRA?": "A Roth IRA is a retirement account where you pay taxes now, not later. Great for young people expecting higher income in the future! You contribute after-tax money, and withdrawals in retirement are tax-free. Want to know more about IRAs?",
    "How do taxes work?": "Taxes are mandatory payments to the government based on your income. The US uses a progressive tax system - the more you earn, the higher your tax rate. You file annually and can get refunds if you overpaid. Need help understanding tax brackets?",
    "What's the difference between stocks and bonds?": "Stocks represent ownership in a company - you're a partial owner! Bonds are loans you make to companies or governments. Stocks are riskier but potentially more rewarding, while bonds are safer but typically lower returns. Want to learn about diversification?",
    "How do I start investing?": "Start small! Open a brokerage account, begin with index funds (they're diversified and low-cost), and invest regularly. Even $25/month can grow significantly over time. Remember: time in the market beats timing the market!",
    "What's compound interest?": "Compound interest is when you earn interest on both your original investment AND previously earned interest. It's like a snowball effect - the longer you invest, the faster your money grows. Albert Einstein called it the 8th wonder of the world!",
    "How do I build an emergency fund?": "Start with $1,000, then build to 3-6 months of expenses. Keep it in a high-yield savings account (not invested). This safety net prevents you from going into debt during unexpected situations like job loss or medical bills."
  };

  const handleSendMessage = async (message = inputValue) => {
    if (!message.trim()) return;

    // If no conversation is selected, create a new one
    let activeConversationId = currentConversationId;
    if (!activeConversationId) {
      try {
        // Pass the message so AI can generate a title
        const newConvResponse = await AskService.createConversation(null, message);
        if (newConvResponse.success) {
          activeConversationId = newConvResponse.data.id;
          setCurrentConversationId(activeConversationId);
          await loadConversations(); // Refresh conversations list
        }
      } catch (error) {
        console.error('Failed to create conversation:', error);
        toast.error('Failed to create conversation');
        return;
      }
    }

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: message,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    
    // Add to previous prompts for quick access
    setPreviousPrompts(prev => [{
      id: userMessage.id,
      content: message,
      timestamp: userMessage.timestamp
    }, ...prev.slice(0, 9)]); // Keep only 10 most recent
    
    setInputValue('');
    setIsLoading(true);

    try {
      console.log('[AskFeature] Sending message:', message);
      
      // Check if AI service is available
      if (!serviceAvailable) {
        console.log('[AskFeature] Service unavailable, checking status...');
        const isAvailable = await AskService.isServiceAvailable();
        setServiceAvailable(isAvailable);
        
        if (!isAvailable) {
          const error = new Error('AI service is currently unavailable. Please check if the backend server is running and try again later.');
          console.error('[AskFeature] Service unavailable:', error);
          throw error;
        }
        console.log('[AskFeature] Service is now available');
      }

      // Send question to backend with conversationId
      console.log('[AskFeature] Calling AskService.askQuestion...');
      const response = await AskService.askQuestion(message, activeConversationId);
      console.log('[AskFeature] Response received:', {
        success: response.success,
        hasData: !!response.data,
        conversationId: response.data?.conversationId,
        requestId: response.requestId
      });
      
      if (response.success) {
        // Update current conversation ID if a new one was created
        if (response.data.conversationId && response.data.conversationId !== activeConversationId) {
          setCurrentConversationId(response.data.conversationId);
        }
        
        const aiMessage = {
          id: response.data.id + '_response',
          type: 'ai',
          content: response.data.answer,
          timestamp: new Date(response.data.createdAt)
        };

        setMessages(prev => [...prev, aiMessage]);
        
        // Refresh conversations list to show updated title (especially after first message)
        // This ensures the title generated from the first prompt is displayed
        await loadConversations();
        
        // Reload messages from database to ensure we have the saved versions
        // The backend saves the message before returning, so it should be available
        // We reload to sync with database, but the user message should already be there
        if (response.data.conversationId) {
          // Small delay to ensure database transaction is committed
          setTimeout(async () => {
            try {
              const historyResponse = await AskService.getHistory(1, 50, response.data.conversationId);
              if (historyResponse.success && historyResponse.data.messages && historyResponse.data.messages.length > 0) {
                const formattedMessages = [];
                const prompts = [];
                
                historyResponse.data.messages.forEach(msg => {
                  if (msg.prompt) {
                    formattedMessages.push({
                      id: msg.id + '_user',
                      type: 'user',
                      content: msg.prompt,
                      timestamp: new Date(msg.createdAt)
                    });
                    prompts.push({
                      id: msg.id,
                      content: msg.prompt,
                      timestamp: new Date(msg.createdAt)
                    });
                  }
                  if (msg.response) {
                    formattedMessages.push({
                      id: msg.id + '_ai',
                      type: 'ai',
                      content: msg.response,
                      timestamp: new Date(msg.createdAt)
                    });
                  }
                });
                
                formattedMessages.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
                prompts.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
                
                setMessages(formattedMessages);
                setPreviousPrompts(prompts);
              }
            } catch (error) {
              console.error('Failed to reload messages:', error);
              // Don't clear messages on error - keep what we have
            }
          }, 300);
        }
        
        soundEffects.playSuccess();
        const sendButton = document.querySelector('.ask-feature-send-button');
        if (sendButton) {
          createSuccessBurst(sendButton);
        }
        toast.success('Question answered successfully!');
        console.log('[AskFeature] Message added to conversation');
      } else {
        const error = new Error(response.message || response.error || 'Failed to get AI response');
        console.error('[AskFeature] Response was not successful:', error, response);
        throw error;
      }
    } catch (error) {
      console.error('[AskFeature] Error in handleSendMessage:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      
      // Determine the type of error and provide appropriate feedback
      let errorMessage = "I apologize, but I'm having trouble processing your request right now.";
      
      if (error.message.includes('Unable to connect to the server')) {
        errorMessage = "Unable to connect to the AI service. Please check if the backend server is running on port 5000.";
      } else if (error.message.includes('AI service is currently unavailable')) {
        errorMessage = "The AI service is currently unavailable. Please try again later or contact support.";
      } else if (error.message.includes('timeout')) {
        errorMessage = "The request timed out. Please try again with a shorter question.";
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Show fallback response
      const fallbackMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: errorMessage + " If the problem persists, please check your internet connection and try again.",
        timestamp: new Date()
      };

      setMessages(prev => [...prev, fallbackMessage]);
      soundEffects.playError();
      toast.error(error.message || 'Failed to get AI response');
    } finally {
      setIsLoading(false);
      console.log('[AskFeature] handleSendMessage completed');
    }
  };

  // Create new conversation
  const handleNewConversation = async () => {
    try {
      soundEffects.playClick();
      const response = await AskService.createConversation();
      if (response.success) {
        setCurrentConversationId(response.data.id);
        setMessages([]);
        setPreviousPrompts([]);
        await loadConversations();
        if (isMobile) setSidebarOpen(false);
        toast.success('New conversation created!');
      }
    } catch (error) {
      console.error('Failed to create conversation:', error);
      toast.error('Failed to create conversation');
    }
  };

  // Switch conversation
  const handleSwitchConversation = async (conversationId) => {
    soundEffects.playClick();
    setCurrentConversationId(conversationId);
    if (isMobile) setSidebarOpen(false);
  };

  // Rename conversation
  const handleRenameConversation = async (conversationId, currentTitle, e) => {
    e.stopPropagation();
    const newTitle = window.prompt('Enter new conversation title:', currentTitle || 'New Conversation');
    
    if (newTitle === null) {
      // User cancelled
      return;
    }
    
    if (!newTitle.trim()) {
      toast.error('Conversation title cannot be empty');
      return;
    }
    
    try {
      soundEffects.playClick();
      const response = await AskService.updateConversation(conversationId, newTitle.trim());
      if (response.success) {
        await loadConversations();
        toast.success('Conversation renamed');
      }
    } catch (error) {
      console.error('Failed to rename conversation:', error);
      toast.error('Failed to rename conversation');
    }
  };

  // Delete conversation
  const handleDeleteConversation = async (conversationId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this conversation? This action cannot be undone.')) {
      return;
    }
    
    try {
      soundEffects.playClick();
      const response = await AskService.deleteConversation(conversationId);
      if (response.success) {
        // If deleted conversation was current, switch to another or clear
        if (conversationId === currentConversationId) {
          const remaining = conversations.filter(c => c.id !== conversationId);
          if (remaining.length > 0) {
            setCurrentConversationId(remaining[0].id);
          } else {
            setCurrentConversationId(null);
            setMessages([]);
            setPreviousPrompts([]);
          }
        }
        await loadConversations();
        toast.success('Conversation deleted');
      }
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      toast.error('Failed to delete conversation');
    }
  };

  const handlePromptClick = (prompt, event) => {
    soundEffects.playClick();
    if (event) {
      createRipple(event.currentTarget, '#3b82f6');
      createPulse(event.currentTarget, 1.05, 200);
    }
    setInputValue(prompt);
    handleSendMessage(prompt);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Convert HTML to plain text by stripping all HTML tags
  const stripHtml = (html) => {
    if (!html) return '';
    
    // First, replace block-level elements with newlines to preserve structure
    let processedHtml = html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n\n')
      .replace(/<p>/gi, '')
      .replace(/<\/div>/gi, '\n')
      .replace(/<div[^>]*>/gi, '');
    
    // Create a temporary DOM element to parse HTML
    const tmp = document.createElement('DIV');
    tmp.innerHTML = processedHtml;
    
    // Get text content (this automatically strips all remaining HTML tags)
    let text = tmp.textContent || tmp.innerText || '';
    
    // Clean up: replace multiple spaces with single space, but preserve newlines
    text = text
      .replace(/[ \t]+/g, ' ')  // Multiple spaces/tabs to single space
      .replace(/\n[ \t]+/g, '\n')  // Remove spaces after newlines
      .replace(/[ \t]+\n/g, '\n')  // Remove spaces before newlines
      .replace(/\n{3,}/g, '\n\n')  // Multiple newlines to double newline
      .trim();
    
    return text;
  };

  // Copy message to clipboard
  const handleCopyMessage = async (content, event) => {
    try {
      soundEffects.playClick();
      if (event) {
        createRipple(event.currentTarget, '#10b981');
      }
      
      // Strip HTML tags and convert to plain text
      const plainText = stripHtml(content);
      
      await navigator.clipboard.writeText(plainText);
      soundEffects.playSuccess();
      toast.success('Message copied to clipboard!');
    } catch (error) {
      soundEffects.playError();
      toast.error('Failed to copy message');
    }
  };

  // Regenerate AI response
  const handleRegenerateResponse = async (messageId, originalPrompt) => {
    try {
      setIsLoading(true);
      
      // Remove the current AI response
      setMessages(prev => prev.filter(msg => msg.id !== messageId));
      
      // Send the original prompt again to get a new response
      await handleSendMessage(originalPrompt);
      
      toast.success('Regenerating response...');
    } catch (error) {
      toast.error('Failed to regenerate response');
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading screen while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect if not authenticated
  if (!isAuthenticated) {
    return null; // Will redirect via useEffect
  }


  return (
    <div className="ask-feature-container">
      {/* Top Bar */}
      <div className="ask-feature-top-bar">
        <div className="ask-feature-top-bar-content">
          <div className="ask-feature-top-bar-left">
            <button 
              onClick={(e) => {
                soundEffects.playClick();
                createRipple(e, '#3b82f6');
                setSidebarOpen(!sidebarOpen);
              }}
              onMouseEnter={() => soundEffects.playHover()}
              className="ask-feature-sidebar-toggle"
              title={sidebarOpen ? "Hide sidebar" : "Show sidebar"}
            >
              {sidebarOpen ? <FaTimes /> : <FaBars />}
            </button>
            <button 
              onClick={(e) => {
                soundEffects.playClick();
                createRipple(e, '#3b82f6');
                navigate('/dashboard');
              }}
              onMouseEnter={() => soundEffects.playHover()}
              className="ask-feature-back-button"
            >
              ←
            </button>
            <h1 className="ask-feature-title">Ask InvestNYou</h1>
          </div>
          <div className="ask-feature-top-bar-right">
            <FaRobot className="ask-feature-ai-icon" />
            <span>AI Assistant</span>
            {!serviceAvailable && (
              <div className="ask-feature-service-unavailable">
                <FaExclamationTriangle className="text-xs" />
                <span className="text-xs">Service Unavailable</span>
              </div>
            )}
            <button 
              onClick={async () => {
                try {
                  const isAvailable = await AskService.isServiceAvailable();
                  setServiceAvailable(isAvailable);
                  
                  if (isAvailable) {
                    toast.success('AI service is available!');
                  } else {
                    toast.error('AI service is not available. Check backend server.');
                  }
                } catch (error) {
                  toast.error('Connection test failed: ' + error.message);
                  console.error('Connection test error:', error);
                }
              }}
              className="ask-feature-test-connection"
            >
              Test Connection
            </button>
            {(process.env.NODE_ENV === 'development' || localStorage.getItem('ask-debug') === 'true') && (
              <button 
                onClick={async () => {
                  try {
                    console.log('🔍 Fetching debug information...');
                    const debugInfo = await AskService.getDebugInfo();
                    console.log('📊 Debug Information:', debugInfo);
                    toast.success('Debug info logged to console. Check browser console.');
                  } catch (error) {
                    console.error('❌ Debug info error:', error);
                    toast.error('Failed to get debug info: ' + error.message);
                  }
                }}
                className="ask-feature-test-connection"
                style={{ marginLeft: '8px' }}
              >
                Debug Info
              </button>
            )}
            <ProfileMenu />
          </div>
        </div>
      </div>

      <div className="ask-feature-main-content">
        {/* Sidebar backdrop (mobile only) */}
        <AnimatePresence>
          {sidebarOpen && isMobile && (
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="ask-feature-sidebar-backdrop"
              onClick={() => setSidebarOpen(false)}
            />
          )}
        </AnimatePresence>

        {/* Sidebar */}
        <AnimatePresence>
          {sidebarOpen && (
            <motion.div
              initial={{ x: -300, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -300, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className={`ask-feature-sidebar ${isMobile ? 'mobile-overlay' : ''}`}
            >
              <div className="ask-feature-sidebar-header">
                <h2 className="ask-feature-sidebar-title">Conversations</h2>
                <button
                  onClick={handleNewConversation}
                  onMouseEnter={() => soundEffects.playHover()}
                  className="ask-feature-new-conversation-button"
                  title="New conversation"
                >
                  <FaPlus />
                </button>
              </div>
              
              <div className="ask-feature-conversations-list">
                {loadingConversations ? (
                  <div className="ask-feature-loading-conversations">
                    <div className="ask-feature-spinner"></div>
                    <span>Loading conversations...</span>
                  </div>
                ) : conversations.length === 0 ? (
                  <div className="ask-feature-no-conversations">
                    <p>No conversations yet</p>
                    <p className="ask-feature-no-conversations-hint">Start a new conversation to begin!</p>
                  </div>
                ) : (
                  conversations.map((conversation) => (
                    <div
                      key={conversation.id}
                      className={`ask-feature-conversation-item ${
                        conversation.id === currentConversationId ? 'active' : ''
                      }`}
                      onClick={() => handleSwitchConversation(conversation.id)}
                      onMouseEnter={(e) => {
                        soundEffects.playHover();
                        createGlow(e.currentTarget, '#3b82f6', 200);
                      }}
                    >
                      <div className="ask-feature-conversation-content">
                        <div className="ask-feature-conversation-title">
                          {conversation.title || 'New Conversation'}
                        </div>
                        <div className="ask-feature-conversation-meta">
                          {conversation.messageCount > 0 && (
                            <span className="ask-feature-conversation-count">
                              {conversation.messageCount} {conversation.messageCount === 1 ? 'message' : 'messages'}
                            </span>
                          )}
                          {conversation.lastMessageAt && (
                            <span className="ask-feature-conversation-time">
                              {new Date(conversation.lastMessageAt).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="ask-feature-conversation-actions">
                        <button
                          className="ask-feature-conversation-rename"
                          onClick={(e) => handleRenameConversation(conversation.id, conversation.title, e)}
                          onMouseEnter={() => soundEffects.playHover()}
                          title="Rename conversation"
                        >
                          <FaEdit />
                        </button>
                        <button
                          className="ask-feature-conversation-delete"
                          onClick={(e) => handleDeleteConversation(conversation.id, e)}
                          onMouseEnter={() => soundEffects.playHover()}
                          title="Delete conversation"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Chat Container */}
        <div className={`ask-feature-chat-container ${sidebarOpen ? 'with-sidebar' : ''}`}>
        {/* Chat Messages */}
        <div 
          className={`ask-feature-messages-area ${messages.length === 0 && !loadingHistory ? 'empty-state' : ''}`}
          onClick={(e) => {
            // Prevent clicks on text/messages from focusing the textarea
            // Only allow focus if clicking directly on the textarea itself
            const target = e.target;
            const isTextarea = target.tagName === 'TEXTAREA' || target.closest('textarea');
            const isButton = target.tagName === 'BUTTON' || target.closest('button');
            const isLink = target.tagName === 'A' || target.closest('a');
            
            // If clicking on text (not textarea, button, or link), prevent any focus behavior
            if (!isTextarea && !isButton && !isLink && textareaRef.current) {
              // Blur textarea if it was focused by accident
              if (document.activeElement === textareaRef.current) {
                textareaRef.current.blur();
              }
            }
          }}
        >
          {loadingHistory && (
            <div className="ask-feature-loading-history">
              <div className="ask-feature-loading-spinner">
                <div className="ask-feature-spinner"></div>
                <span className="text-sm">Loading conversation history...</span>
              </div>
            </div>
          )}
          
          {!serviceAvailable && (
            <div className="ask-feature-service-warning">
              <div className="ask-feature-warning-content">
                <FaExclamationTriangle />
                <span className="font-medium">AI Service Unavailable</span>
              </div>
              <p className="ask-feature-warning-text">
                The AI assistant is currently unavailable. Please try again later or contact support if the issue persists.
              </p>
            </div>
          )}
          
          {messages.length === 0 && !loadingHistory && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="ask-feature-welcome"
            >
              <div className="ask-feature-welcome-icon">
                <FaRobot className="text-2xl text-blue-600" />
              </div>
              <h3 className="ask-feature-welcome-title">
                Hi! I'm your financial learning assistant
              </h3>
              <p className="ask-feature-welcome-subtitle">
                Ask me anything about personal finance, investing, budgeting, and more!
              </p>
              
              <div className="ask-feature-prompts-grid">
                {preloadedPrompts.map((prompt, index) => (
                  <button
                    key={index}
                    onClick={(e) => handlePromptClick(prompt, e)}
                    onMouseEnter={(e) => {
                      soundEffects.playHover();
                      createGlow(e.currentTarget, '#3b82f6', 200);
                    }}
                    className="ask-feature-prompt-button"
                  >
                    {prompt}
                  </button>
                ))}
              </div>
              
              {/* Previous Prompts */}
              {previousPrompts.length > 0 && (
                <div className="ask-feature-previous-prompts">
                  <h4 className="ask-feature-previous-title">
                    <FaHistory className="ask-feature-previous-icon" />
                    <span>Your Previous Questions</span>
                  </h4>
                  <div className="ask-feature-previous-grid">
                    {previousPrompts.slice(0, 5).map((prompt, index) => (
                      <button
                        key={prompt.id}
                        onClick={(e) => handlePromptClick(prompt.content, e)}
                        onMouseEnter={(e) => {
                          soundEffects.playHover();
                          createGlow(e.currentTarget, '#3b82f6', 200);
                        }}
                        className="ask-feature-previous-button"
                      >
                        <div className="ask-feature-previous-content">{prompt.content}</div>
                        <div className="ask-feature-previous-timestamp">
                          {prompt.timestamp.toLocaleDateString()} at {prompt.timestamp.toLocaleTimeString([], { 
                            hour: '2-digit', 
                            minute: '2-digit',
                            hour12: true 
                          })}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}



          <AnimatePresence>
            {messages.map((message, index) => {
              const isUser = message.type === 'user';
              const prevMessage = index > 0 ? messages[index - 1] : null;
              const showAvatar = !prevMessage || prevMessage.type !== message.type;
              const showTime = index === messages.length - 1 || 
                              (index < messages.length - 1 && 
                               messages[index + 1].type !== message.type);



              return (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className={`ask-feature-message-container ${isUser ? 'user' : 'ai'}`}
                >
                  {isUser ? (
                    // User message layout - RIGHT SIDE
                    <div className="ask-feature-message-wrapper">
                      {/* Message Content */}
                      <div className="ask-feature-message-content">
                        {/* Message Bubble */}
                        <div className="ask-feature-message-bubble user">
                          <div className="ask-feature-message-text">
                            {message.content}
                          </div>
                          
                          {/* Message Tail */}
                          <div className="ask-feature-message-tail user"></div>
                        </div>
                        
                        {/* Timestamp */}
                        {showTime && (
                          <div className="ask-feature-message-timestamp">
                            <div className="ask-feature-timestamp-text">
                              {message.timestamp.toLocaleTimeString([], { 
                                hour: '2-digit', 
                                minute: '2-digit',
                                hour12: true 
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Avatar */}
                      {showAvatar && (
                        <div className="ask-feature-avatar user">
                          <FaUser />
                        </div>
                      )}
                    </div>
                  ) : (
                    // AI message layout - LEFT SIDE
                    <div className="ask-feature-message-wrapper ai">
                      {/* Avatar */}
                      {showAvatar && (
                        <div className="ask-feature-avatar ai">
                          <FaRobot />
                        </div>
                      )}
                      
                      {/* Message Content */}
                      <div className="ask-feature-message-content ai">
                        {/* Message Bubble */}
                        <div className="ask-feature-message-bubble ai">
                          <div 
                            className="ask-feature-message-text ai"
                            dangerouslySetInnerHTML={{ __html: message.content }}
                          />
                          
                          {/* Message Tail */}
                          <div className="ask-feature-message-tail ai"></div>
                        </div>
                        
                        {/* Timestamp and Actions */}
                        {showTime && (
                          <div className="ask-feature-message-timestamp ai">
                            <div className="ask-feature-timestamp-text ai">
                              {message.timestamp.toLocaleTimeString([], { 
                                hour: '2-digit', 
                                minute: '2-digit',
                                hour12: true 
                              })}
                            </div>
                            
                            {/* Action buttons for AI messages */}
                            <div className="ask-feature-message-actions">
                              <button 
                                className="ask-feature-action-button" 
                                title="Copy to clipboard"
                                onClick={(e) => handleCopyMessage(message.content, e)}
                                onMouseEnter={() => soundEffects.playHover()}
                              >
                                📋
                              </button>
                              <button 
                                className="ask-feature-action-button" 
                                title="Regenerate response"
                                onClick={(e) => {
                                  soundEffects.playClick();
                                  createRipple(e, '#3b82f6');
                                  // Find the original user prompt for this AI response
                                  const messageIndex = messages.findIndex(msg => msg.id === message.id);
                                  if (messageIndex > 0) {
                                    const userMessage = messages[messageIndex - 1];
                                    if (userMessage.type === 'user') {
                                      handleRegenerateResponse(message.id, userMessage.content);
                                    }
                                  }
                                }}
                                onMouseEnter={() => soundEffects.playHover()}
                              >
                                🔄
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </AnimatePresence>

          {isLoading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="ask-feature-typing-container"
            >
              <div className="ask-feature-typing-wrapper">
                {/* AI Avatar */}
                <div className="ask-feature-avatar ai">
                  <FaRobot />
                </div>
                
                {/* Typing Indicator */}
                <div className="ask-feature-typing-bubble">
                  <div className="ask-feature-typing-content">
                    <div className="ask-feature-typing-text">
                      <span className="text-sm text-gray-600">AI is typing</span>
                      <div className="ask-feature-typing-dots">
                        <div className="ask-feature-typing-dot"></div>
                        <div className="ask-feature-typing-dot"></div>
                        <div className="ask-feature-typing-dot"></div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Message Tail */}
                  <div className="ask-feature-typing-tail"></div>
                </div>
              </div>
            </motion.div>
          )}

          <div ref={messagesEndRef} />
          
          {/* Previous Prompts - Show when there are messages */}
          {messages.length > 0 && previousPrompts.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="ask-feature-previous-section"
            >
              <h4 className="ask-feature-previous-section-title">
                <FaHistory className="ask-feature-previous-section-icon" />
                <span>Previous Questions</span>
              </h4>
              <div className="ask-feature-previous-section-buttons">
                {previousPrompts.slice(0, 3).map((prompt) => (
                  <button
                    key={prompt.id}
                    onClick={(e) => handlePromptClick(prompt.content, e)}
                    onMouseEnter={(e) => {
                      soundEffects.playHover();
                      createGlow(e.currentTarget, '#3b82f6', 200);
                    }}
                    className="ask-feature-previous-section-button"
                  >
                    {prompt.content.length > 50 ? prompt.content.substring(0, 50) + '...' : prompt.content}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </div>

        {/* Input */}
        <div className="ask-feature-input-container">
          <div className="ask-feature-input-wrapper">
            <div className="ask-feature-input-field">
              <textarea
                ref={textareaRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                onClick={(e) => {
                  // Stop event propagation to prevent any parent click handlers
                  e.stopPropagation();
                }}
                placeholder={serviceAvailable ? "Type your question here..." : "AI service is currently unavailable"}
                disabled={!serviceAvailable}
                className={`ask-feature-textarea ${
                  !serviceAvailable ? 'disabled' : ''
                }`}
                rows="1"
                style={{ minHeight: '48px', maxHeight: '120px' }}
              />
              <button
                onClick={(e) => {
                  if (inputValue.trim() && !isLoading && serviceAvailable) {
                    soundEffects.playClick();
                    createRipple(e, '#3b82f6');
                    createPulse(e.currentTarget, 1.1, 200);
                    handleSendMessage();
                  } else {
                    soundEffects.playError();
                  }
                }}
                onMouseEnter={(e) => {
                  if (inputValue.trim() && !isLoading && serviceAvailable) {
                    soundEffects.playHover();
                    createGlow(e.currentTarget, '#3b82f6', 300);
                  }
                }}
                disabled={!inputValue.trim() || isLoading || !serviceAvailable}
                className={`ask-feature-send-button ${
                  !inputValue.trim() || isLoading || !serviceAvailable ? 'disabled' : ''
                }`}
              >
                <FaPaperPlane className="text-sm" />
              </button>
            </div>
          </div>
          
          <div className="ask-feature-input-footer">
            <div className="ask-feature-input-footer-left">
              <span>💡 Educational information only</span>
              {serviceAvailable && (
                <div className="ask-feature-status-indicator">
                  <div className="ask-feature-status-dot"></div>
                  <span>AI Online</span>
                </div>
              )}
            </div>
            <div className="text-gray-400">
              Press Enter to send
            </div>
          </div>
        </div>
      </div>
      </div>

      {/* Bottom Nav */}
      <BottomNav />
    </div>
  );
};

export default AskFeature;
